import { useCallback, useEffect, useRef } from "react";

export type AuctionRefreshOptions = {
  /** Refresh after a hidden-to-visible transition. Defaults to true. */
  refreshOnReturn?: boolean;
};

/** Browser lifecycle only; each hook keeps ownership of its data and stale-response guard. */
export function useAuctionReturnRefresh(
  refresh: () => void,
  { refreshOnReturn = true }: AuctionRefreshOptions
) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const lifecycle = useRef<{
    active: boolean;
    queued: boolean;
    reserved: boolean;
    pending: Set<symbol>;
  }>({
    active: false,
    pending: new Set<symbol>(),
    queued: false,
    reserved: false,
  });
  const schedule = useCallback(() => {
    const { current } = lifecycle;
    if (
      // biome-ignore lint/suspicious/noUnnecessaryConditions: Effects and visibility callbacks mutate this ref after the callback is created.
      !(current.active && current.queued) ||
      current.reserved ||
      current.pending.size > 0 ||
      document.visibilityState !== "visible"
    ) {
      return;
    }
    // Reserve before React starts the next effect, so repeated events coalesce.
    current.reserved = true;
    refreshRef.current();
  }, []);

  useEffect(() => {
    const { current } = lifecycle;
    current.active = refreshOnReturn;
    if (!refreshOnReturn) {
      return;
    }
    let hidden = document.visibilityState === "hidden";
    const onVisibilityChange = () => {
      const wasHidden = hidden;
      hidden = document.visibilityState === "hidden";
      if (wasHidden && !hidden) {
        current.queued = true;
        schedule();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      current.active = false;
      current.queued = false;
      current.reserved = false;
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshOnReturn, schedule]);

  const startRead = useCallback(() => {
    const { current } = lifecycle;
    const request = Symbol("auction read");
    current.pending.add(request);
    // A request started after a return satisfies that return, including a manual read.
    current.queued = false;
    current.reserved = false;
    return () => {
      current.pending.delete(request);
      schedule();
    };
  }, [schedule]);
  return startRead;
}
